import { FC, useState } from 'react';
import { Box, CircularProgress, IconButton, Paper, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { DownloadSimpleIcon } from '@phosphor-icons/react';

import { getChatDocumentDownloadUrl } from '../api/documents';
import { extractFileExtension, getFileIconByExtension } from '../utils/fileUtils';

export interface MessageAttachmentProps {
  documentId: string;
  fileName: string;
  s3Key: string;
  conversationId: string;
}

// const formatFileSize = (bytes: number | undefined): string => {
//   if (!bytes) return '';

//   if (bytes < 1024) return `${bytes} B`;
//   if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
//   return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
// };

export const MessageAttachment: FC<MessageAttachmentProps> = observer(
  ({ documentId, fileName, s3Key, conversationId }) => {
    const { t } = useTrans();
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    const handleDownload = async () => {
      try {
        setIsDownloading(true);
        setDownloadError(null);

        const fileExtension = extractFileExtension(fileName);
        const { s3DownloadUrl } = await getChatDocumentDownloadUrl(
          documentId,
          conversationId,
          fileExtension,
        );

        // Fetch as blob so the download attribute works across origins (no new tab)
        const blob = await fetch(s3DownloadUrl).then((r) => r.blob());
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('[MessageAttachment] Download failed:', error);
        setDownloadError(
          error instanceof Error ? error.message : t('chat.attachment.downloadError'),
        );
      } finally {
        setIsDownloading(false);
      }
    };

    return (
      <Paper
        variant="outlined"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          pr: 1,
          backgroundColor: 'background.paper',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: '320px',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        {/* File Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            flexShrink: 0,
          }}
        >
          {getFileIconByExtension(fileName)}
        </Box>

        {/* File Info */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={fileName}
          >
            {fileName}
          </Typography>
          {downloadError && (
            <Typography
              variant="caption"
              sx={{
                color: 'error.main',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {downloadError}
            </Typography>
          )}
        </Box>

        {/* Download Button */}
        <IconButton
          onClick={handleDownload}
          disabled={isDownloading}
          size="small"
          sx={{
            color: 'primary.main',
            flexShrink: 0,
            '&:hover': {
              backgroundColor: 'primary.light',
            },
            '&.Mui-disabled': {
              color: 'text.disabled',
            },
          }}
          aria-label={t('chat.attachment.download')}
        >
          {isDownloading ? <CircularProgress size={20} /> : <DownloadSimpleIcon size={20} />}
        </IconButton>
      </Paper>
    );
  },
);
